import type { LeaderboardEntry } from "@app/protocol";
import type { useT } from "../i18n/context";

type Translate = ReturnType<typeof useT>["t"];

const HOUR_MS = 3_600_000;

/** "3 days 4 hours left" until the period turns; nothing for all time. */
export function timeLeft(t: Translate, endsAt: number | null, now: number): string | null {
  if (endsAt === null) return null;
  const hours = Math.max(0, Math.floor((endsAt - now) / HOUR_MS));
  const days = Math.floor(hours / 24);
  if (days > 0) return t("leaderboard.left.days", { days, hours: hours % 24 });
  if (hours > 0) return t("leaderboard.left.hours", { hours });
  return t("leaderboard.left.soon");
}

export interface TrendLook {
  /** What shows: "▲ 6", "▼ 2", a bar for no change, "new". */
  readonly glyph: string;
  /** What a screen reader says. */
  readonly name: string;
  readonly tone: "up" | "down" | "flat" | "new";
}

/** Places gained since the period before, as an arrow; nothing for all time. */
export function trendLook(t: Translate, trend: LeaderboardEntry["trend"]): TrendLook | null {
  if (trend === null) return null;
  if (trend === "new") {
    const word = t("leaderboard.trend.new");
    return { glyph: word, name: word, tone: "new" };
  }
  if (trend > 0)
    return { glyph: `▲ ${trend}`, name: t("leaderboard.trend.up", { n: trend }), tone: "up" };
  if (trend < 0)
    return { glyph: `▼ ${-trend}`, name: t("leaderboard.trend.down", { n: -trend }), tone: "down" };
  return { glyph: "\u2014", name: t("leaderboard.trend.same"), tone: "flat" };
}
